import React, { useEffect, useState } from "react";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import {
    getClient,
    getCacheClients,
} from "../API/clientAPI";
import {
    Typography,
    Grid,
    Autocomplete,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    Card,
    CardHeader,
    CardContent,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import { getEmailExceptionsByClientId, postEmailException, postEmailExceptionChanges } from "../API/emailExceptionAPI";
import { useLocation } from "react-router-dom";
import { useRef } from "react";

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));


function ManageEmailException() {
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false); // State to manage loading
    const [clientsCache, setClientsCache] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [client, setClient] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isFetchingClient, setIsFetchingClient] = useState(false);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [exceptions, setExceptions] = useState([]);
    // const [exceptionList, setExceptionList] = useState([])
    const [isEdit, setIsEdit] = useState(false)
    const location = useLocation();
    const searchButtonRef = useRef(null);
    const [autoSearch, setAutoSearch] = useState(false);

    const clientFilter = createFilterOptions({
        matchFrom: "any",                     // allow substring matches, not just prefix
        stringify: (option) => option?.name || "",
    });

    // useEffect(() => {
    //     const fetchData = async () => {
    //         try {
    //             setIsLoading(true);
    //             setClientsLoading(true);
    //             setError(null);

    //             const [rawClients, exceptionList] = await Promise.all([
    //                 getCacheClients(),
    //                 getEmailExceptions(),
    //             ]);

    //             // normalize + de-dupe by ID, keep same-name/different-ID rows
    //             const cleaned = (Array.isArray(rawClients) ? rawClients : []).map(c => ({
    //                 ...c,
    //                 name: c?.name ? c.name.trim() : c?.name,
    //             }));
    //             const byId = new Map();
    //             for (const c of cleaned) {
    //                 if (c?.id && !byId.has(c.id)) byId.set(c.id, c);
    //             }
    //             const unique = Array.from(byId.values()).sort((a,b)=> (a.name||"").localeCompare(b.name||""));
    //             setClientsCache(unique);
    //             setExceptionList(exceptionList);
    //         } catch (err) {
    //             setError(err.message);
    //         } finally {
    //             setIsLoading(false);
    //             setClientsLoading(false);
    //         }
    //     };

    //     fetchData();
    // }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
            setIsLoading(true);
            setClientsLoading(true);
            setError(null);

            const rawClients = await getCacheClients();

            // normalize + de-dupe by ID, keep same-name/different-ID rows
            const cleaned = (Array.isArray(rawClients) ? rawClients : []).map(c => ({
                ...c,
                name: c?.name ? c.name.trim() : c?.name,
            }));
            const byId = new Map();
            for (const c of cleaned) {
                if (c?.id && !byId.has(c.id)) byId.set(c.id, c);
            }
            const unique = Array.from(byId.values()).sort((a, b) =>
                (a.name || "").localeCompare(b.name || "")
            );

            setClientsCache(unique);
            } catch (err) {
            setError(err.message);
            } finally {
            setIsLoading(false);
            setClientsLoading(false);
            }
        };

        fetchData();
    }, []);


    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const presetId = params.get("clientId");
        if (presetId && clientsCache.length) {
            const preset = clientsCache.find((c) => c.id === presetId);
            if (preset) {
                setSelectedClient(preset);
                setAutoSearch(true);
            }
        }
    }, [location.search, clientsCache]);

    useEffect(() => {
        if (autoSearch && selectedClient) {
            searchButtonRef.current?.click();
            setAutoSearch(false);
        }
    }, [autoSearch, selectedClient]);


    const handleSearchClient = (event, value) => {
        setIsFormVisible(false);
        setSelectedClient(value || null);
    };

    const handleSearchButtonClick = async () => {
        if (!selectedClient) {
            console.log("No client selected");
            return;
        }
        if (isFetchingClient) {
            console.log("Already fetching client details...");
            return;
        }

        setIsFetchingClient(true);
        setIsLoading(true);

        try {
            const fetched = await getClient(selectedClient.id);
            setClient(fetched);
            setIsFormVisible(true);

            // filter → always returns an array (empty if no matches)
            // const clientExceptions = exceptionList.filter(
            //     (ex) => ex.clientId === (fetched.id ?? selectedClient.id)
            // );
            // setExceptions(clientExceptions);
            // setIsEdit(clientExceptions.length > 0);
            const clientId = fetched?.id ?? selectedClient.id;
            const clientExceptions = await getEmailExceptionsByClientId(clientId);

            const safeList = Array.isArray(clientExceptions) ? clientExceptions : [];
            setExceptions(safeList);
            setIsEdit(safeList.length > 0);

        } catch (error) {
            console.error("Error fetching client details:", error);
        } finally {
            setIsFetchingClient(false);
            setIsLoading(false);
        }

    };


    // Returns an onChange handler for a specific emailAddress and exceptionType
    const handleExceptionChange =
        (emailAddress, exceptionType, contactId, contactFullName) =>
            (event) => {
                const checked = event.target.checked;

                setExceptions((prev) => {
                    // pull out the existing record (if any) and the rest of the list
                    const existing = prev.find((item) => item.email === emailAddress);
                    const others = prev.filter((item) => item.email !== emailAddress);

                    // build the new exceptions array
                    const updatedExceptions = existing
                        ? checked
                            ? Array.from(new Set([...existing.exceptions, exceptionType]))
                            : existing.exceptions.filter((e) => e !== exceptionType)
                        : checked
                            ? [exceptionType]
                            : [];

                    // if nothing remains, just return the rest
                    if (updatedExceptions.length === 0) {
                        return others;
                    }

                    // otherwise, return the rest + updated record:
                    return [
                        ...others,
                        {
                            // keep every field your backend gave you (including `id`)
                            ...existing,
                            // but always overwrite:
                            clientId: client.id,
                            clientName: client.name,
                            contactId,
                            contact: contactFullName,
                            email: emailAddress,
                            exceptions: updatedExceptions,
                        },
                    ];
                });
            };


    async function handleSubmit(e) {
        e.preventDefault();

        // console.log(exceptions)
        try {
            const result = await (
            isEdit
                ? postEmailExceptionChanges({
                    clientId: client.id,   // scope the sync
                    new: exceptions,       // only this client’s final list
                })
                : postEmailException(exceptions)
            );


            if (result) {
                console.log("Success:", result);
                // reset form or show a message…
                window.location.href = "/list-email-exceptions";
            } else {
                console.error("API call returned no data");
            }
        } catch (err) {
            console.error("Error syncing email exceptions:", err);
        }
    }

    return (

        <BaseFormWrapper
            titleIcon={<SupervisedUserCircleIcon />}
            title="Client Retention"
            subTitle="List Email Exceptions"
            subTitleRoute="/list-email-exceptions"
            formTitle="Manage Email Exception"
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >
            <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6} lg={6} xl={4}>
                        <Autocomplete
                            options={clientsCache}
                            getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            filterOptions={(options, params) => clientFilter(options, params)}
                            openOnFocus
                            autoHighlight
                            value={selectedClient} // Controlled component
                            onChange={(event, newValue) =>
                                handleSearchClient(event, newValue)
                            } // Updates selected value
                            loading={clientsLoading}                            
                            noOptionsText="No clients found"
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <span>{option.name}</span>
                                        <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                                    </div>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Client Name"
                                    variant="outlined"
                                    fullWidth
                                    error={!!error} // Displays error styling if there is an error
                                    helperText={error || ""} // Displays error message
                                />
                            )}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        lg={6}
                        xl={6}
                        container
                        alignItems={"center"}
                    >
                        <Button
                            ref={searchButtonRef}
                            variant="contained"
                            color="primary"
                            onClick={handleSearchButtonClick}
                            disabled={isLoading}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <StyledHr />
            {isFormVisible && client && (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit(e);
                    }}
                    noValidate
                    autoComplete="off"
                >
                    <Grid container spacing={3}>
                        <Grid
                            item
                            xs={12}
                            md={12}
                            sx={{ display: "flex", flexDirection: "column" }}
                        >
                            <Card
                                variant="outlined"
                                sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                            >
                                <CardHeader
                                    title={
                                        <Typography
                                            variant="h6"
                                        >
                                            Basic Information
                                        </Typography>
                                    }
                                />
                                <CardContent
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <Grid container spacing={3}>
                                        {/* Client Name */}
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Client Name"
                                                name="clientName"
                                                value={client.name}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                disabled
                                                size="small"
                                            />
                                        </Grid>

                                        {/* Client ID (directly below Client Name) */}
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                label="Client ID"
                                                value={client.id || ""}
                                                fullWidth
                                                size="small"
                                                disabled
                                                sx={{ width: "12ch" }}
                                            />
                                        </Grid>

                                        {/* Contact Cards */}
                                        <Grid item xs={12}>
                                            <Grid container spacing={2}>
                                                {client.contacts.map((contact, index) => {
                                                    const contactFullName = `${contact.firstname} ${contact.lastname}`;
                                                    const contactId = contact.id;

                                                    return (
                                                        <Grid item xs={12} key={index}>
                                                            <Card variant="outlined">
                                                                <CardContent sx={{ padding: "16px" }}>
                                                                    <Grid container spacing={2}>
                                                                        <Grid item xs={12} md={6}>
                                                                            <TextField
                                                                                label="First Name"
                                                                                fullWidth
                                                                                value={contact.firstname}
                                                                                size="small"
                                                                                disabled
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={6}>
                                                                            <TextField
                                                                                label="Last Name"
                                                                                fullWidth
                                                                                value={contact.lastname}
                                                                                size="small"
                                                                                disabled
                                                                            />
                                                                        </Grid>

                                                                        {contact.emails.map((emailObj, emailIdx) => {
                                                                            const emailAddress = emailObj.email;
                                                                            // Look up an existing entry for this email, if any
                                                                            const entry = exceptions.find(
                                                                                (item) => item.email === emailAddress
                                                                            );
                                                                            const hasAdvanced =
                                                                                entry?.exceptions.includes("advanced_job_notification") ||
                                                                                false;
                                                                            const hasCompletion =
                                                                                entry?.exceptions.includes("job_completion_notification") ||
                                                                                false;
                                                                            const hasFirstOfMonth =
                                                                                entry?.exceptions.includes("first_of_month_notification") ||
                                                                                false;

                                                                            return (
                                                                                <Grid
                                                                                    item
                                                                                    xs={12}
                                                                                    md={8}
                                                                                    key={`email-${index}-${emailIdx}`}
                                                                                >
                                                                                    <TextField
                                                                                        label="Email Address"
                                                                                        fullWidth
                                                                                        value={emailAddress}
                                                                                        size="small"
                                                                                        disabled
                                                                                    />
                                                                                    <FormControlLabel
                                                                                        control={
                                                                                            <Checkbox
                                                                                                checked={hasAdvanced}
                                                                                                onChange={handleExceptionChange(
                                                                                                    emailAddress,
                                                                                                    "advanced_job_notification",
                                                                                                    contactId,
                                                                                                    contactFullName
                                                                                                )}
                                                                                            />
                                                                                        }
                                                                                        label="Advanced Job Notification"
                                                                                        sx={{ mt: 1 }}
                                                                                    />
                                                                                    <FormControlLabel
                                                                                        control={
                                                                                            <Checkbox
                                                                                                checked={hasCompletion}
                                                                                                onChange={handleExceptionChange(
                                                                                                    emailAddress,
                                                                                                    "job_completion_notification",
                                                                                                    contactId,
                                                                                                    contactFullName
                                                                                                )}
                                                                                            />
                                                                                        }
                                                                                        label="Job Completion Notification"
                                                                                        sx={{ mt: 1 }}
                                                                                    />
                                                                                    <FormControlLabel
                                                                                        control={
                                                                                            <Checkbox
                                                                                                checked={hasFirstOfMonth}
                                                                                                onChange={handleExceptionChange(
                                                                                                    emailAddress,
                                                                                                    "first_of_month_notification",
                                                                                                    contactId,
                                                                                                    contactFullName
                                                                                                )}
                                                                                            />
                                                                                        }
                                                                                        label="First of Month Notification"
                                                                                        sx={{ mt: 1 }}
                                                                                    />
                                                                                </Grid>
                                                                            );
                                                                        })}
                                                                    </Grid>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Grid>
                                    </Grid>




                                </CardContent>
                            </Card>

                        </Grid>

                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            mt={2}
                            mb={2}
                            mr={2}
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => window.location.reload()}
                                sx={{
                                    fontSize: "0.875rem",
                                    color: "#FF4C4C",
                                    borderColor: "#FF4C4C",
                                }}
                                disabled={isLoading} // Optionally disable the button while loading
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                sx={{
                                    marginLeft: "10px",
                                    fontSize: "0.875rem",
                                }}
                                disabled={isLoading} // Optionally disable the button while loading
                            >
                                {isLoading ? "Submitting..." : "Submit"}
                            </Button>
                        </Grid>


                    </Grid>
                </form>
            )}
        </BaseFormWrapper>
    );
}


export default ManageEmailException;